import { escapeHtml } from '../../utils/format';

// 6x4in box labels, one per box ("Box 3/27"), printed from a popup window like the
// production sheet. They carry only the delivery address and the customer's own
// reference: no Ultimat details, no SO number and no phone or email, because the
// boxes go to the end customer and often ship under the trade customer's name.

// Priority part numbers that are charges rather than goods, so they get no label
// (matches NO_PICK_PARTS in the priority-order-webhook function)
export const NO_LABEL_PARTS = ['DEL'];

const isChargeLine = (item) => NO_LABEL_PARTS.includes(
  String(item.originalItemId || item.itemId || '').toUpperCase()
);

// Suggested box count: one per unit on the order, delivery charge excluded.
// The operator can change it before printing (several frames may share a box).
export const countLabelBoxes = (items) => items
  .filter(item => !isChargeLine(item))
  .reduce((sum, item) => sum + (Number(item.quantity) || 0), 0);

export const MAX_LABEL_BOXES = 500;

// The country line is left off for UK deliveries, where it adds nothing for the courier.
const UK_NAMES = ['UNITED KINGDOM', 'UK', 'GB', 'GREAT BRITAIN', 'ENGLAND', 'SCOTLAND', 'WALES', 'NORTHERN IRELAND'];

export const addressLines = (address) => {
  const country = (address.country || '').trim();
  return [
    address.company,
    ...(address.lines || []),
  ]
    .map(line => (line || '').trim())
    // No contact details on the box: an email typed into an address field is dropped
    .filter(line => line && !line.includes('@'))
    .concat(country && !UK_NAMES.includes(country.toUpperCase()) ? [country] : []);
};

const labelHtml = (address, reference, box, total) => `
  <section class="label">
    <div class="top"><span>DELIVER TO</span><span>${reference ? `Ref ${escapeHtml(reference)}` : ''}</span></div>
    <div class="addr">
      ${address.name ? `<b>${escapeHtml(address.name)}</b><br>` : ''}
      ${addressLines(address).map(escapeHtml).join('<br>')}
      ${address.postcode ? `<br><b class="pc">${escapeHtml(address.postcode)}</b>` : ''}
    </div>
    <div class="box">BOX <b>${box}/${total}</b></div>
  </section>`;

export const buildBoxLabelsHtml = ({ orderId, address, reference = '', boxes }) => {
  const total = Math.max(1, Math.min(MAX_LABEL_BOXES, Math.floor(Number(boxes) || 0)));
  const labels = Array.from({ length: total }, (_, i) => labelHtml(address, reference, i + 1, total)).join('');
  return `
    <!DOCTYPE html>
    <html>
    <head>
      <title>Box labels - ${escapeHtml(orderId)}</title>
      <style>
        @page { size: 6in 4in; margin: 0; }
        * { box-sizing: border-box; }
        html, body { margin: 0; padding: 0; }
        body { font-family: Helvetica, Arial, sans-serif; color: #000; }
        .label { width: 6in; height: 4in; padding: 0.25in 0.3in; position: relative; overflow: hidden; page-break-after: always; break-after: page; }
        .label:last-child { page-break-after: auto; break-after: auto; }
        .top { display: flex; justify-content: space-between; font-size: 11pt; font-weight: 700; letter-spacing: 0.08em; border-bottom: 2px solid #000; padding-bottom: 4px; }
        .addr { margin-top: 12px; font-size: 18pt; line-height: 1.24; max-width: 3.9in; }
        .addr .pc { font-size: 24pt; }
        .addr small { font-size: 10pt; }
        .box { position: absolute; right: 0.3in; bottom: 0.3in; border: 3px solid #000; padding: 6px 14px; font-size: 16pt; text-align: center; }
        .box b { display: block; font-size: 42pt; line-height: 1; }
      </style>
    </head>
    <body>${labels}</body>
    </html>
  `;
};
